import React, { useEffect, useMemo, useState } from "react";
import { Card, Flex, HelpIndicator, QueryLoader, Tag, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import {
  ScreeningMatchResult,
  ComplianceCaseStatus as CaseStatus,
  ComplianceCaseTypeEnum,
} from "~/graphql/supergraphEnums";
import { useGetCaseProgramQuery } from "~/graphql/supergraphHooks";
import { type ComplianceCaseStatus, type ScreeningMatchType } from "~/graphql/supergraphTypes";
import { formatShortFormDate } from "~/utils/datetime";
import { ComplianceClientType } from "../../types";
import { getScreeningResult, sortGroupedMatches } from "../LexisNexisMatches/utils";
import FilterDropDown from "./FilterDropDown";
import {
  activeButton,
  externalLinkIcon,
  filterDropdown,
  screeningRecordTimeline,
  screeningTimelineCard,
} from "./styles_vanilla.css";

interface ScreeningTimelineProps {
  caseId: number;
  matches: ScreeningMatchType[];
  setActiveMatches: (matches: ScreeningMatchType[]) => void;
  caseStatus?: ComplianceCaseStatus;
  positionId?: string;
}

const RESULT_MAPPING: Record<string, string> = {
  "True Match": ScreeningMatchResult.TrueMatch,
  "False Positive": ScreeningMatchResult.FalsePositive,
  "Gathering Information": ScreeningMatchResult.GatheringInformation,
};

const ScreeningTimeline: React.FC<ScreeningTimelineProps> = ({
  caseId,
  matches,
  setActiveMatches,
  caseStatus,
  positionId,
}) => {
  const [isActive, setIsActive] = useState<number | null>(null);

  const caseProgram = useGetCaseProgramQuery({
    variables: { id: caseId },
  });

  const isPICR =
    caseProgram.data?.complianceCase?.caseType ===
    ComplianceCaseTypeEnum.PaymentInstructionChangeReview;

  const picrPositionDetail =
    isPICR && positionId
      ? caseProgram?.data?.picrPositionDetailsByCaseId.find(
          (element) => element.positionId === positionId,
        )
      : undefined;

  const caseProgramName = useMemo(() => {
    if (isPICR) {
      if (positionId && picrPositionDetail?.linkedOnboardingCase?.program) {
        return [picrPositionDetail.linkedOnboardingCase.program.name];
      }
      // When positionId is undefined, return all program names from all positions
      const allProgramNames = caseProgram.data?.picrPositionDetailsByCaseId
        ?.map((position) => position.linkedOnboardingCase?.program?.name)
        .filter((name): name is string => !!name);
      return Array.from(new Set(allProgramNames ?? []));
    }

    const programName = caseProgram.data?.complianceCase?.program?.name;
    return programName && programName !== "None" ? [programName] : [];
  }, [
    isPICR,
    caseProgram.data?.complianceCase?.program?.name,
    caseProgram.data?.picrPositionDetailsByCaseId,
    positionId,
    picrPositionDetail?.linkedOnboardingCase?.program,
  ]);

  const getIcons = (result: string) => {
    switch (result) {
      case "Potential Matches":
        return "exclamation-triangle";
      case "True Match":
        return "exclamation-circle";
      default:
        return "check-circle";
    }
  };

  const resultOptions: string[] = ["False Positive", "True Match", "Gathering Information"];
  const [selectedResults, setSelectedResults] = useState<string[]>([]);

  const clients: ComplianceClientType[] = useMemo(() => {
    const clientMap = new Map<number, ComplianceClientType>();
    matches
      .flatMap((match) => match.clients)
      .forEach((client) =>
        clientMap.set(client.id, { id: client.id, name: client.name || "--" }),
      );
    return Array.from(clientMap.values());
  }, [matches]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);

  const selectedClientNames = clients
    .filter((client) => selectedClientIds.includes(client.id))
    .map((client) => client.name);

  const programs: string[] = useMemo(() => {
    const matchesPrograms = matches.flatMap((match) => match.programs);
    const allPrograms = [...matchesPrograms, ...caseProgramName];
    return Array.from(new Set(allPrograms));
  }, [matches, caseProgramName]);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);

  // Pre-select case program name only once when it becomes available
  useEffect(() => {
    if (caseProgramName.length > 0) {
      setSelectedPrograms(caseProgramName);
    } else {
      setSelectedPrograms(programs);
    }
  }, [caseProgramName, programs]);

  const filteredMatches = useMemo(() => {
    // If no programs and clients are selected, return empty array
    if (selectedPrograms.length === 0 && selectedClientIds.length == 0) {
      return [];
    }

    return matches.filter((match) => {
      if (selectedResults.length > 0) {
        const values = selectedResults.map(
          (result) => RESULT_MAPPING[result] ?? ScreeningMatchResult.Unknown,
        );
        if (!values.includes(match.result)) {
          return false;
        }
      }
      if (
        selectedClientIds.length > 0 &&
        !selectedClientIds.some((selectedClientId) =>
          match.clients.some((client) => client.id === selectedClientId),
        )
      ) {
        return false;
      }
      if (
        selectedPrograms.length > 0 &&
        !selectedPrograms.some((program) => match.programs.includes(program))
      ) {
        return false;
      }
      return true;
    });
  }, [matches, selectedResults, selectedClientIds, selectedPrograms]);

  // Group matches by caseId, and matches without caseId in a separate group
  const groupedMatches = useMemo(() => {
    const grouped = new Map<number, ScreeningMatchType[]>();

    filteredMatches.forEach((match) => {
      const groupKey = match.caseId ?? -1;
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, []);
      }
      grouped.get(groupKey)!.push(match);
    });

    return grouped;
  }, [filteredMatches]);

  const sortedGroupedMatches = useMemo(() => {
    return sortGroupedMatches(groupedMatches);
  }, [groupedMatches]);

  // Reset active selection when filters change
  useEffect(() => {
    setIsActive(null);
    setActiveMatches([]);
  }, [selectedResults, selectedClientIds, selectedPrograms]);

  useEffect(() => {
    // Only set initial state if nothing is currently active
    if (isActive === null && sortedGroupedMatches.length > 0) {
      const firstKey = sortedGroupedMatches[0][0];
      const hasCaseId = sortedGroupedMatches.some(([groupKey]) => groupKey === caseId);
      const currentEvaluationKey = hasCaseId
        ? caseId
        : (sortedGroupedMatches.find(([groupKey]) => groupKey === -1)?.[0] ?? firstKey);

      if (currentEvaluationKey !== undefined) {
        const currentEvaluationMatches = groupedMatches.get(currentEvaluationKey) || [];
        setIsActive(currentEvaluationKey);
        setActiveMatches(currentEvaluationMatches);
      }
    }
  }, [matches, caseId, sortedGroupedMatches, groupedMatches, isActive]);

  return (
    <QueryLoader result={caseProgram}>
      <Card className={screeningTimelineCard}>
        <Text className={filterDropdown} variant="h4">
          Screening timeline
        </Text>
        <Flex className={filterDropdown} flexDirection="row" gap={Sizes.SM} alignItems="center">
          <FilterDropDown
            label="Result"
            options={resultOptions}
            selectedOptions={selectedResults}
            setSelectedOptions={setSelectedResults}
          />
          <FilterDropDown
            label="Client"
            options={clients.map((client) => client.name)}
            selectedOptions={selectedClientNames}
            setSelectedOptions={(selectedNames) => {
              const namesToProcess =
                typeof selectedNames === "function"
                  ? selectedNames(selectedClientNames)
                  : selectedNames;
              setSelectedClientIds(
                clients
                  .filter((client) => namesToProcess.includes(client.name))
                  .map((client) => client.id),
              );
            }}
          />
          <FilterDropDown
            label="Program"
            options={programs}
            selectedOptions={selectedPrograms}
            setSelectedOptions={setSelectedPrograms}
          />
        </Flex>
        {sortedGroupedMatches.length > 0 ? (
          sortedGroupedMatches.map(([groupKey, matches]) => {
            const isLatestTimeline = groupKey === -1;
            const resultOfCumulativeMatches = getScreeningResult(matches);
            const modifiedAt: string | undefined =
              matches
                .filter((match: ScreeningMatchType) => match.modifiedAt)
                .sort(
                  (a, b) =>
                    new Date(b.modifiedAt ?? "").getTime() -
                    new Date(a.modifiedAt ?? "").getTime(),
                )[0]?.modifiedAt ?? undefined;

            return (
              <Flex
                key={groupKey}
                className={isActive === groupKey ? activeButton : screeningRecordTimeline}
                flexDirection="row"
                onClick={() => {
                  setIsActive(groupKey);
                  setActiveMatches(matches);
                }}
                alignItems="center"
                gap={8}
                justifyContent="space-between"
              >
                <Flex flexDirection="row" gap={8} alignItems="center">
                  <Icon
                    data-testid="timeline-icon"
                    type={getIcons(resultOfCumulativeMatches)}
                    variant={isActive === groupKey ? "solid" : "outline"}
                    color={isActive === groupKey ? Colors.Primary700 : undefined}
                  ></Icon>
                  <Flex flexDirection="column">
                    <Flex flexDirection="row" gap={4} alignItems="center">
                      {isLatestTimeline ? (
                        <Text variant="regular-md">Current Evaluation</Text>
                      ) : (
                        <Text variant="regular-md">
                          {modifiedAt ? formatShortFormDate(modifiedAt) : "--"}
                        </Text>
                      )}
                      {!isLatestTimeline && caseId != groupKey && (
                        <HelpIndicator
                          id={groupKey.toString()}
                          placement="top"
                          content="View case"
                        >
                          <Icon
                            data-testid="external-link"
                            className={externalLinkIcon}
                            type="external-link"
                            color={Colors.Primary600}
                            onClick={() => {
                              window.open(
                                `/canopy/compliance/case/${groupKey}`,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }}
                          ></Icon>
                        </HelpIndicator>
                      )}
                    </Flex>
                    <Text variant="secondary-sm">{resultOfCumulativeMatches}</Text>
                  </Flex>
                </Flex>
                {groupKey === -1 && <Tag variant="warning">Review Needed</Tag>}
              </Flex>
            );
          })
        ) : (
          <Flex className={activeButton} alignItems="center" gap={8}>
            <Icon
              data-testid="no-matches-icon"
              type="check-circle"
              variant="solid"
              color={Colors.Primary700}
            />
            <Flex flexDirection="column">
              <Text>
                {caseStatus != CaseStatus.Closed
                  ? `Current Evaluation`
                  : `Previously Evaluated`}
              </Text>
              <Text variant="secondary-sm">No Matches Found</Text>
            </Flex>
          </Flex>
        )}
      </Card>
    </QueryLoader>
  );
};

export default ScreeningTimeline;
